/**
 * Single source of truth for how a cardio prescription is worded.
 * Used by the weekly view tile, the day panel, the active workout and the end card
 * so the wording can never drift between screens.
 */

export const isRoundsBlock = (block) => !!block?.rounds_protocol;

/** A rounds block split across 2-3 movements */
export const isMixedRounds = (block) => (block?.rounds_segments?.length || 0) > 1;

export const mixMovementNames = (block) =>
  (block?.rounds_segments || []).map((s) => s.modality_name);

const workLabel = (seconds) =>
  seconds % 60 === 0 ? `${seconds / 60} minute` : `${seconds} second`;

/** "6 sets of 1 minute rounds" */
export function formatCardioLong(block) {
  if (isMixedRounds(block)) return mixMovementNames(block).join(' + ');
  const r = block?.rounds_protocol;
  if (r) return `${r.sets} sets of ${workLabel(r.work_duration_seconds)} rounds`;
  const ip = block?.interval_protocol;
  if (ip) return `${ip.rounds} × ${ip.work_duration_seconds}s work / ${ip.rest_duration_seconds}s rest`;
  return `${block?.duration_minutes || 0} min ${(block?.session_type || 'steady') === 'recovery' ? 'recovery' : 'steady'}`;
}

/** Short form for the day tile — "6×1m", "8×40s", "20m" */
export function formatCardioShort(block) {
  if (isMixedRounds(block)) return `${block.rounds_segments.length} moves`;
  const r = block?.rounds_protocol;
  if (r) {
    const w = r.work_duration_seconds;
    return `${r.sets}×${w % 60 === 0 ? `${w / 60}m` : `${w}s`}`;
  }
  const ip = block?.interval_protocol;
  if (ip) return `${ip.rounds}×${ip.work_duration_seconds}s`;
  return `${block?.duration_minutes || 0}m`;
}

/** "17 min total · 45s recovery" style support line */
export function formatCardioSub(block) {
  if (isMixedRounds(block)) {
    return `${block.duration_minutes} min total · ${block.rounds_segments
      .map((s) => `${s.rounds_protocol?.sets}×${s.modality_name}`)
      .join(' · ')}`;
  }
  const r = block?.rounds_protocol;
  if (r) return `${r.total_duration_minutes} min total · ${r.rest_duration_seconds}s recovery`;
  return null;
}
